<template>
	<div class="CodeEditor">

		<div class="editor">
			<editor-content class="editor__content" :editor="editor" />
		</div>

		<!-- Variable popup -->
		<template >
			<div class="suggestion-list" v-show="showSuggestions" ref="suggestions">
				<template v-if="hasResults">
				<div
					v-for="(variabletag, index) in filteredVariabletags"
					:key="variabletag.id"
					class="suggestion-list__item"
					:class="{ 'is-selected': navigatedVariableTagIndex === index }"
					@click="selectVariableTag(variabletag)"
				>
					{{ variabletag.name }}
				</div>
				</template>
				<div v-else class="suggestion-list__item is-empty">
					No matching variable found<!-- [create?] -->
				</div>
			</div>
		</template>

	</div>
</template>

<script>
//-----------------------------------------------------------------------------------------

import Fuse from 'fuse.js'
import tippy from 'tippy.js'
import Variables from '../utils/Variables';
import { Slice } from 'prosemirror-model'

import {
	Editor,
	EditorContent,
	EditorMenuBar,
	Doc,
	Text,
} from 'tiptap'

import {
	History,
	Placeholder,
} from 'tiptap-extensions'

import {
	VariableTag,
	Codeline,
} from '../editor-modules';


var discovered_tags = []; // used before component.data is formed
var last_pasted_text = '';

export default {

	components: {
		EditorContent,
		EditorMenuBar,
	},

	props : [
		'disabled',
		'value',
		'variables',
		'controls',
		'onFocus',
		'onBlur',
		'editable_variables',
	],

	data() {
		var self = this;

		var create_item = {
							id:'ADD',
							name:'Create new',
						};

		var editor_extensions = [
			new Doc(),
			new Text(),
			new Codeline(),
			new History(),
			new Placeholder({
				emptyClass: 'is-empty',
				emptyNodeText: 'Enter your config code here …',
				showOnlyWhenEditable: true,
			}),
		];

		if( self.editable_variables || self.variables){
			editor_extensions.push(new VariableTag({

				// a list of all suggested items
				items: function(){
					var r = (self.variables || []).reduce(function(a,v){
						a.push({
							id : v.lid,
							name : v.label,
						});
						a = (v.subvariables||[]).reduce((a,sv)=>{
							a.push({
								id : sv.lid,
								name : sv.label,
							})
							return a;
						},a)
						return a;
					},[]);
					if(self.editable_variables){
						r.push(create_item);
					}
					return r;
				},

				// is called when a suggestion starts
				onEnter: ({
					items, query, range, command, virtualNode,
				}) => {
					this.query = query
					this.filteredVariabletags = items
					this.suggestionRange = range
					this.renderPopup(virtualNode)
					// we save the command for inserting a selected mention
					// this allows us to call it inside of our custom popup
					// via keyboard navigation and on click
					this.insertVariabletag = command
				},

				// is called when a suggestion has selected
				onCreateClick: (data) => {
					data = data || {query : this.query};
					document.activeElement.blur();
					this.$emit('command',{label:'create-var-and-insert',data});
				},


				// is called when a suggestion has changed
				onChange: ({
					items, query, range, virtualNode,
				}) => {
					this.query = query
					this.filteredVariabletags = items
					this.suggestionRange = range
					this.navigatedVariableTagIndex = 0
					this.renderPopup(virtualNode)
				},

				// is called when a suggestion is cancelled
				onExit: () => {
					// reset all saved values
					this.query = null
					this.filteredVariabletags = []
					this.suggestionRange = null
					this.navigatedVariableTagIndex = 0
					this.destroyPopup()
				},

				// is called on every keyDown event while a suggestion is active
				onKeyDown: ({ event }) => {
					// pressing up arrow
					if (event.keyCode === 38) {
						this.upHandler()
						return true
					}
					// pressing down arrow
					if (event.keyCode === 40) {
						this.downHandler()
						return true
					}
					// pressing enter
					if (event.keyCode === 13) {
						this.enterHandler()
						return true
					}

					return false
				},

				// is called when a suggestion has changed
				// this function is optional because there is basic filtering built-in
				// you can overwrite it if you prefer your own filtering
				// in this example we use fuse.js with support for fuzzy search
				onFilter: (items, query) => {
					if (!query) {
						return items
					}

					const fuse = new Fuse(items.filter(i=>i.id!='ADD'), {
						threshold: 0.2,
						keys: ['name'],
					})

					var result = fuse.search(query);
					if(self.editable_variables){
						result.push(create_item);
					}
					return result;
				},
			}));

		}


		var editor_options = {
			
			editable : !this.disabled,

			parseOptions : { // Prosemirror variables
				preserveWhitespace : "full",
			},
			editorProps : { // Prosemirror variables
				preserveWhitespace : "full",
				
				/**
				 * This was a saga - couldn't get the parser to properly manage pasted HTML, 
				 * preserving whitespace. Needed to remove the standard paste handling and 
				 * manage it manually using the plaintext representation in the paste which
				 * seems to give the truest whitespace.
				 */
				handlePaste: function(view,e,slice){
					var text = (e.originalEvent || e).clipboardData.getData('text/plain');
					if(!text) return; // Allow default if you can't detect text

					var newslicejson = slice;
					newslicejson.content=[];

					text.split("\n").map( line => {
						newslicejson.content.push({
							type: "codeline",
							content : line?[{
								type : "text",
								text : line,
							}]:[],
						})
					});

					var newslice = Slice.fromJSON(self.editor.schema,newslicejson);


					const transaction = self.editor.state.tr.replaceSelection(newslice);
					self.editor.view.dispatch(transaction)

					return true;
				},

				/**
				 * Natural code editor tab behaviour. Sorry Web Standards
				 */
				handleKeyDown(view,e){

					switch(e.keyCode){
						case 9: // tab
							e.preventDefault();
							const transaction = self.editor.state.tr.insertText("\t");
							self.editor.view.dispatch(transaction);
							return true;
							break;
					}

					return false;
				},

				// clipboardSerializer(){
				// 	console.log('clipboardSerializer');
				// },

				clipboardTextSerializer(slice){
					var json = slice.toJSON();
					return (json.content||[]).map((line)=>{
						return (line.content||[]).map((section)=>{
							switch(section.type){
								case 'text' : return section.text;
								case 'variabletag' : return '{{'+section.attrs.label+'}}';
							}
							return '';
						}).join('');
					}).join("\r\n");
				},
			},

			useBuiltInExtensions : false,
			extensions: editor_extensions,

			content: this.setUpContent(self.value),

			onUpdate:function(state){
				self.updateFromState(state)
			},

			
			// onDrop(view, event, slice, moved) {

				
			// 	// event.preventDefault()
			// 	// const coordinates = view.posAtCoords({ left: event.clientX, top: event.clientY })
			// 	// console.log(event.dataTransfer);
			// 	const content = event.dataTransfer.getData('text/html')
			// 	console.log(content);
			// 	// const transaction = view.state.tr.insertText(content, coordinates.pos)
			// 	// view.dispatch(transaction)
				
			// 	// return !moved;
			// },
		};

		if(this.onFocus){
			editor_options.onFocus = function(){ self.onFocus(); };
		}

		if(this.onBlur){
			editor_options.onBlur = function(){ self.onBlur(); };
		}

		return {
			string : '',
			query: null,
			suggestionRange: null,
			filteredVariabletags: [],
			navigatedVariableTagIndex: 0,
			insertVariabletag: () => {},
			editor: new Editor(editor_options),
		} // End Data
	},

	computed: {
		hasResults() {
			return this.filteredVariabletags.length
		},
		showSuggestions() {
			return this.query || this.hasResults
		},
	},

	watch: {
		disabled : function(){
			this.editor.setOptions({editable: !this.disabled});
		},
		value : function(){
		},
		variables : function(){
			this.syncVartags();
		},
	},

	mounted(){
		var self = this;

		discovered_tags.map(function(tag){
			self.$emit('command',{label:'variabletag-add',data:tag});
		})
		this.syncVartags();
	},

	methods: {

		/*
		 *
		 * This function will link up any tags without a local ID (lid) to a variable with a matching label
		 * If the tag already has a lid, it will be kept up-to-date with that variable
		 * If there is no match, the tag will present as errored
		 *
		 */
		syncVartags(){
			let self = this;
			let dispatch = this.editor.view.dispatch;
			let state = this.editor.state;

			// Start a transaction
			let tr = state.tr;
			
			tr.setMeta("addToHistory",false); // Don't allow undo of allocation

			// Loop through Nodes
			state.doc.descendants((node,pos)=>{
				// If Node is vartag...
				if(node.type.name=='variabletag'){
					
					var lid_match_var = self.variables.reduce( (a,c) => {
						if(a) return a; // Pass forward if already found
						if(c.lid == node.attrs.id) return c; // Return if found
						return (c.subvariables||[]).find((v)=>v.lid==node.attrs.id) || false; // Return found subvariable
					},false);


					// ... and there is a matching variable with lid...
					if(lid_match_var){
						
						// ... create a transation to update the Node
						tr.setNodeMarkup(pos,undefined,{
							id : lid_match_var.lid,
							label : lid_match_var.label,
						});
					} else {
						var var_label = node.attrs.label;
						var var_first_order_label = node.attrs.label.split('.').shift();
						var var_is_sublabel = node.attrs.label.split('.').length > 1;

						var	label_match_var = self.variables.find((v)=>v.label==var_first_order_label);	
						
						if(var_is_sublabel){
							label_match_var = (label_match_var.subvariables||[]).find((v)=>v.label==var_label);
						}

						if(label_match_var){
							tr.setNodeMarkup(pos,undefined,{
								id : label_match_var.lid,
								label : label_match_var.label,
							});
						} else {
							tr.setNodeMarkup(pos,undefined,{
								id : '404',
								label : 'VARIABLE MISSING',
							});
						}

					}
				}
			})

			// Commit transaction
			dispatch(tr);
		},


		updateFromState(state){
			var jso = state.getJSON();
			if(jso){
				this.string = this.parseJsDomSegmentToString(jso);
				this.$emit('input', this.string);
			}
		},

		parseJsDomSegmentToString(segment){
			var self = this;
			segment = segment || {};
			switch(segment.type){
				case 'text':
					return segment.text || '';
				case 'variabletag':
					return '{{' + ((segment.attrs||{}).label || 'ERR') + '}}';
				default:
					return (segment.content||[]).reduce(function(c,o){
						c.push(self.parseJsDomSegmentToString(o));
						return c;
					},[]).join(segment.type == 'doc' ? "\n" : '');
			}
		},

		command(cdata){
			switch( (cdata||{}).label ){
				// case 'body-sync-variables':
				// 	this.syncVartags();
				// 	break;
				case 'body-insert-variable':
					var tag = (cdata||{}).data;
					if(tag) this.insertVariableTag(tag);
					break;
			}
		},

		insertVariableTag(variable){
			this.editor.commands.variabletag({
				label : variable.label,
				id : variable.lid,
			})
		},

		// navigate to the previous item
		// if it's the first item, navigate to the last one
		upHandler() {
			this.navigatedVariableTagIndex = ((this.navigatedVariableTagIndex + this.filteredVariabletags.length) - 1) % this.filteredVariabletags.length
		},

		// navigate to the next item
		// if it's the last item, navigate to the first one
		downHandler() {
			this.navigatedVariableTagIndex = (this.navigatedVariableTagIndex + 1) % this.filteredVariabletags.length
		},

		enterHandler() {
			const variabletag = this.filteredVariabletags[this.navigatedVariableTagIndex]

			if (variabletag) {
				this.selectVariableTag(variabletag)
			}
		},

		// we have to replace our suggestion text with a mention
		// so it's important to pass also the position of your suggestion text
		selectVariableTag(variabletag) {
			this.insertVariabletag({
				range: this.suggestionRange,
				attrs: {
					id: variabletag.id,
					label: variabletag.name,
				},
			})
			this.editor.focus()
		},

		// renders a popup with suggestions
		// tiptap provides a virtualNode object for using popper.js (or tippy.js) for popups
		renderPopup(node) {
			if (this.popup) {
			return
			}

			this.popup = tippy(node, {
				content: this.$refs.suggestions,
				trigger: 'mouseenter',
				interactive: true,
				theme: 'dark',
				placement: 'top-start',
				// performance: true,
				inertia: true,
				duration: [400, 200],
				sticky: true,
				showOnInit: true,
				arrow: true,
				arrowType: 'round',
			})
		},

		destroyPopup() {
			if (this.popup) {
			this.popup.destroy(true)
			this.popup = null
			}
		},

		setUpContent(string){
			var self = this;
			
			// string = string.replace(/\t/g,'&emsp;');
			
			// Escape html characters
			var temp_node = document.createElement('div');
			temp_node.appendChild(document.createTextNode(string));
			string = temp_node.innerHTML;

			// Convert lines to <div>s
			var lines = string.split(/\n/);
			lines = lines.map(function(line){
				return line.replace( /\{\{([^}]+)\}\}/g ,function(m,p1){ return self.parseInnerTag(p1); });
			})
			string = '<div>' + lines.join('</div><div>') + '</div>';
			
			return string;
		},


		// tidyVariableertyLabel : function(string){
		// 	string = string.trim();
		// 	string = string.replace(/[^A-Za-z0-9._]/g,'_');
		// 	return string;
		// },

		parseInnerTag(string){
			var variable_match = Variables.findVariable(string,Variables.expand(this.variables));
			if(!variable_match) return '{{'+string+'}}';
			// string = string.trim();
			// string = this.tidyVariableertyLabel(string);
			discovered_tags.push(string);
			return '<span data-variable-id="'+variable_match.lid+'" >' + variable_match.label + '</span>';
		},

	},
}
</script>
